import { HoverExplanation, StatusPill } from './BatiwebForwardCell';

// Colonne « Marketplace » de l'onglet No match : le lead est-il visible des acheteurs sur la Bourse
// du Lead ? Calculé côté back avec les mêmes règles que la Bourse (services/marketplaceVisibility.ts).

export type MarketplaceHiddenReason =
    | 'not_no_match'
    | 'bought'
    | 'forwarded'
    | 'batiweb_sending'
    | 'batiweb_pending'
    | 'batiweb_sold'
    | 'no_seller'
    | 'expired'
    | 'no_price';

export interface MarketplaceVisibility {
    visible: boolean;
    reason?: MarketplaceHiddenReason;
    until?: string;
}

const REASONS: Record<MarketplaceHiddenReason, { short: string; meaning: string }> = {
    not_no_match: { short: "N'est plus en no-match", meaning: "Le lead n'est plus en no-match : seule la Bourse du Lead propose les leads no-match." },
    bought: { short: 'Déjà acheté', meaning: 'Un acheteur a déjà acheté ce lead.' },
    forwarded: { short: 'Transféré', meaning: "Le lead a été transféré à un acheteur depuis le back-office : c'est sa copie qui est vendue, l'original est retiré de la Bourse." },
    batiweb_sending: { short: 'Envoi Batiweb en cours', meaning: "Le lead est en cours d'envoi à Batiweb : il est retiré de la Bourse pour ne pas être vendu deux fois." },
    batiweb_pending: { short: 'En vente chez Batiweb', meaning: "Le lead est en cours de vente chez Batiweb : il est retiré de la Bourse tant qu'ils ne l'ont pas refusé." },
    batiweb_sold: { short: 'Vendu à Batiweb', meaning: 'Batiweb a acheté ce lead.' },
    no_seller: { short: 'Sans vendeur', meaning: "Le lead n'a pas de vendeur : il ne peut pas être vendu sur la Bourse." },
    expired: { short: 'Expiré (+14 j)', meaning: 'Un lead reste sur la Bourse 14 jours après sa création.' },
    no_price: { short: 'Sans prix vendeur', meaning: "Aucun prix minimum vendeur n'a pu être déterminé pour ce lead : la Bourse ne peut pas l'afficher." },
};

const shortDate = (value?: string) => (value ? new Date(value).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : null);

const MarketplaceVisibilityCell = ({ marketplace }: { marketplace?: MarketplaceVisibility | null }) => {
    if (!marketplace) return <span className="text-gray-300">—</span>;
    const reason = marketplace.reason ? REASONS[marketplace.reason] : null;

    return (
        <div className="flex flex-col gap-1">
            <HoverExplanation
                title={marketplace.visible ? 'Visible sur la Bourse du Lead' : 'Pas visible sur la Bourse du Lead'}
                trigger={
                    <span>
                        <StatusPill tone={marketplace.visible ? 'green' : 'gray'} label={marketplace.visible ? 'Oui' : 'Non'} hollow={!marketplace.visible} />
                    </span>
                }
            >
                {marketplace.visible ? (
                    <p>Les acheteurs peuvent voir et acheter ce lead sur la Bourse du Lead jusqu'au {shortDate(marketplace.until)}.</p>
                ) : (
                    <p>{reason?.meaning}</p>
                )}
            </HoverExplanation>
            <span className="whitespace-nowrap pl-0.5 text-[10px] leading-4 text-[#8A93A3] dark:text-[#7E8798]">
                {marketplace.visible
                    ? `Jusqu'au ${shortDate(marketplace.until)}`
                    : marketplace.reason === 'expired'
                      ? `Expiré le ${shortDate(marketplace.until)}`
                      : reason?.short}
            </span>
        </div>
    );
};

export default MarketplaceVisibilityCell;
